/**
 * FranchiseSuspensionPage — `/franchise/suspension`.
 *
 * Two-pane operator tool:
 *   1. Pending-suspension request queue (operations slice 4b):
 *      `GET /v1/franchise/ops/suspension-requests`
 *      `POST /v1/franchise/ops/suspension-requests/:id/review` to approve
 *      (auto-suspends) or reject.
 *   2. Currently-SUSPENDED roster + unsuspend control:
 *      `GET /v1/franchise/franchises?status=SUSPENDED`
 *      `POST /v1/franchise/franchises/:id/unsuspend`
 */
import { type JSX, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import {
  createSuspensionRequest,
  listFranchises,
  listSuspensionRequests,
  reviewSuspensionRequest,
  unsuspendFranchise,
} from '../../lib/api/franchise';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';

export function FranchiseSuspensionPage(): JSX.Element {
  const qc = useQueryClient();
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [showRequest, setShowRequest] = useState(false);
  const [requestForm, setRequestForm] = useState({ franchiseId: '', reason: '' });

  // Pending-suspension request queue (NEW — replaces the deferred-notice).
  const requests = useQuery({
    queryKey: ['franchise', 'suspension', 'requests', 'PENDING'],
    queryFn: () => listSuspensionRequests({ status: 'PENDING', pageSize: 100 }),
    retry: false,
  });

  // Currently-suspended franchises.
  const list = useQuery({
    queryKey: ['franchise', 'suspension', 'list'],
    queryFn: () => listFranchises({ pageSize: 100, status: 'SUSPENDED' }),
    retry: false,
  });

  // Live franchises (for the request-form picker).
  const eligible = useQuery({
    queryKey: ['franchise', 'suspension', 'eligible'],
    queryFn: () => listFranchises({ pageSize: 100, status: 'LIVE' }),
    retry: false,
  });

  const fileRequest = useMutation({
    mutationFn: () => {
      if (requestForm.franchiseId.length === 0 || requestForm.reason.length < 3) {
        throw new Error('Pick a franchise and supply a reason (min 3 chars).');
      }
      return createSuspensionRequest({
        franchiseId: requestForm.franchiseId,
        reason: requestForm.reason,
      });
    },
    onSuccess: () => {
      setShowRequest(false);
      setRequestForm({ franchiseId: '', reason: '' });
      void qc.invalidateQueries({ queryKey: ['franchise', 'suspension', 'requests'] });
    },
    onError: (e: Error) => setActionErr(e.message),
  });

  const review = useMutation({
    mutationFn: (input: { id: string; decision: 'APPROVE' | 'REJECT'; note: string }) =>
      reviewSuspensionRequest(input.id, input.decision, input.note),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['franchise', 'suspension'] });
    },
    onError: (e: Error) => setActionErr(e.message),
  });

  const unsuspend = useMutation({
    mutationFn: (id: string) => unsuspendFranchise(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['franchise', 'suspension', 'list'] });
    },
    onError: (e: Error) => setActionErr(e.message),
  });

  const denied =
    list.isError && list.error instanceof ApiError &&
    (list.error.status === 401 || list.error.status === 403);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Suspensions"
        description="Pending suspension requests + currently-suspended roster."
        actions={
          <button
            type="button"
            onClick={() => setShowRequest((v) => !v)}
            className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark"
          >
            {showRequest ? 'Cancel' : 'File suspension request'}
          </button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPICard title="Pending requests" value={String(requests.data?.total ?? 0)} />
        <KPICard
          title="Currently suspended"
          value={String(list.data?.items.length ?? 0)}
        />
        <KPICard
          title="Eligible (LIVE)"
          value={String(eligible.data?.items.length ?? 0)}
          hint="Available to file a request against"
        />
      </div>

      {showRequest ? (
        <div className="bg-white border border-border rounded-xl p-4 space-y-3">
          <p className="text-sm font-semibold text-gray-900">File a suspension request</p>
          <div>
            <label className="block text-xs font-medium text-muted mb-1">Franchise</label>
            <select
              value={requestForm.franchiseId}
              onChange={(e) => setRequestForm({ ...requestForm, franchiseId: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm"
            >
              <option value="">Select a LIVE franchise…</option>
              {eligible.data?.items.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.legalName} ({f.franchiseNumber})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted mb-1">Reason</label>
            <textarea
              value={requestForm.reason}
              onChange={(e) => setRequestForm({ ...requestForm, reason: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg text-sm"
            />
          </div>
          <button
            type="button"
            onClick={() => {
              setActionErr(null);
              fileRequest.mutate();
            }}
            disabled={fileRequest.isPending}
            className="px-3 py-2 bg-nipost-green text-white text-sm font-semibold rounded-lg hover:bg-nipost-green-dark disabled:opacity-50"
          >
            {fileRequest.isPending ? 'Submitting…' : 'Submit request'}
          </button>
        </div>
      ) : null}

      {actionErr ? <p className="text-sm text-danger">{actionErr}</p> : null}

      <section className="space-y-3" data-testid="suspension-request-queue">
        <h2 className="text-sm font-semibold text-gray-900">Pending suspension requests</h2>
        {requests.isPending ? (
          <p className="text-sm text-muted">Loading queue…</p>
        ) : (requests.data?.items.length ?? 0) === 0 ? (
          <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
            No pending suspension requests.
          </p>
        ) : (
          <div className="bg-white border border-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="text-left px-4 py-2">Franchise ref</th>
                  <th className="text-left px-4 py-2">Reason</th>
                  <th className="text-left px-4 py-2">Requested</th>
                  <th className="text-right px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {requests.data?.items.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-[11px] text-muted">
                      {r.franchiseId.slice(0, 8)}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-700">{r.reason}</td>
                    <td className="px-4 py-2 text-xs text-muted">
                      {new Date(r.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-right space-x-3">
                      <button
                        type="button"
                        onClick={() => {
                          setActionErr(null);
                          review.mutate({
                            id: r.id,
                            decision: 'APPROVE',
                            note: 'Approved via operator console',
                          });
                        }}
                        disabled={review.isPending}
                        className="text-xs font-semibold text-nipost-green hover:underline disabled:opacity-50"
                      >
                        Approve →
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setActionErr(null);
                          review.mutate({
                            id: r.id,
                            decision: 'REJECT',
                            note: 'Rejected via operator console',
                          });
                        }}
                        disabled={review.isPending}
                        className="text-xs font-semibold text-danger hover:underline disabled:opacity-50"
                      >
                        Reject ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="space-y-3" data-testid="suspended-roster">
        <h2 className="text-sm font-semibold text-gray-900">Currently suspended</h2>
        {list.isPending ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : denied ? (
          <DeferredNotice title="Permission required">
            <code className="font-mono">Franchise/read</code> required.
          </DeferredNotice>
        ) : list.isError ? (
          <DeferredNotice title="Service unreachable">{(list.error as Error).message}</DeferredNotice>
        ) : (list.data?.items.length ?? 0) === 0 ? (
          <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
            No franchises currently suspended.
          </p>
        ) : (
          <div className="bg-white border border-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="text-left px-4 py-2">Franchise</th>
                  <th className="text-left px-4 py-2">Tier</th>
                  <th className="text-left px-4 py-2">State / LGA</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">Suspended</th>
                  <th className="text-right px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {list.data?.items.map((f) => (
                  <tr key={f.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-xs">
                      <Link
                        to={`/franchise/franchises/${encodeURIComponent(f.id)}`}
                        className="block font-medium text-gray-900 hover:underline"
                      >
                        {f.legalName}
                      </Link>
                      <span className="font-mono text-[11px] text-muted">{f.franchiseNumber}</span>
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-700">{f.tierCode}</td>
                    <td className="px-4 py-2 text-xs text-gray-700">
                      {f.state} / {f.lga}
                    </td>
                    <td className="px-4 py-2">
                      <StatusBadge status={f.status} />
                    </td>
                    <td className="px-4 py-2 text-xs text-muted">
                      {f.suspendedAt ? new Date(f.suspendedAt).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => {
                          setActionErr(null);
                          unsuspend.mutate(f.id);
                        }}
                        disabled={unsuspend.isPending}
                        className="text-xs font-semibold text-nipost-green hover:underline disabled:opacity-50"
                      >
                        Unsuspend →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
